import { useState, useEffect } from "react";

import {
  ArrowLeft,
  Clock,
  Users,
  Coins,
  CheckCircle,
  Circle,
  ExternalLink,
  Twitter,
  MessageCircle,
  Users as UsersIcon,
  Heart,
  Share,
  UserPlus,
  Copy,
  CheckCheck,
  AlertCircle,
  Instagram,
  CopyIcon,
  DollarSign,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  useGetProfileApiCallQuery,
  useGetTokenPriceConversionApiCallQuery,
  useSendOtpApiCallMutation,
  useVerifyOtpApiCallMutation,
} from "@/services/api/auth/auth.api";
import { Campaign } from "@/services/api/campaigns/campaigns.type";
import { Badge } from "@/components/ui/badge";
import { get } from "http";
import { getTimeRemaining } from "@/utils/getTimeRemaining";
import {
  useGetTasksApiCallQuery,
  useVerifyTaskApiCallMutation,
} from "@/services/api/tasks/tasks.api";
import {
  clientId,
  getCodeChallenge,
  redirectUri,
} from "@/components/Twitter/Twitter";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { ApiError } from "@/services/api/auth/auth.type";
import { Task } from "@/services/api/tasks/tasks.type";
import { generateRandomString, scope } from "@/components/instagram/Instagram";
import { useViewSocialsCredentialsApiCallQuery } from "@/services/api/campaigns/campaigns.api";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import Loader from "@/components/Loader";
import { DASHBOARD_PATH } from "@/constants/constant";

interface TaskTest {
  id: number;
  title: string;
  description: string;
  type:
    | "twitter_follow"
    | "twitter_like"
    | "twitter_retweet"
    | "twitter_share"
    | "telegram_join"
    | "discord_join"
    | "invite_friends"
    | "verify_account";
  reward: number;
  completed: boolean;
  url?: string;
  requiresConnection?: boolean;
  socialPlatform?: "twitter" | "telegram" | "discord";
}

interface CampaignTest {
  id: number;
  name: string;
  description: string;
  detailedDescription: string;
  totalReward: number;
  totalPool: string;
  participants: number;
  timeLeft: string;
  endDate: string;
  difficulty: "Easy" | "Medium" | "Hard";
  status: "Active" | "Completed" | "Ended";
  progress: number;
  tasks: TaskTest[];
  rules: string[];
  requirements: string[];
}

interface SocialConnection {
  platform: "twitter" | "telegram" | "discord" | "instagram";
  connected: boolean;
  username?: string;
}

// Mock campaign data
const mockCampaign: CampaignTest = {
  id: 1,
  name: "BINGOLD Genesis Launch",
  description:
    "Join our genesis launch campaign and earn exclusive BINGOLD tokens.",
  detailedDescription:
    "Welcome to the BINGOLD Genesis Launch campaign! This is your opportunity to earn exclusive BINGOLD tokens by completing various social media tasks. Help us build a strong community and spread awareness about BINGOLD while earning rewards for your participation.",
  totalReward: 500,
  totalPool: "50,000",
  participants: 1234,
  timeLeft: "3 days left",
  endDate: "2024-02-15",
  difficulty: "Easy",
  status: "Active",
  progress: 60,
  tasks: [
    {
      id: 1,
      title: "Follow @BingoldOfficial on Twitter",
      description:
        "Follow our official Twitter account to stay updated with the latest news",
      type: "twitter_follow",
      reward: 100,
      completed: true,
      url: "https://twitter.com/BingoldOfficial",
      requiresConnection: true,
      socialPlatform: "twitter",
    },
    {
      id: 2,
      title: "Like our Genesis Launch announcement",
      description: "Like our pinned tweet about the Genesis Launch campaign",
      type: "twitter_like",
      reward: 75,
      completed: true,
      url: "https://twitter.com/BingoldOfficial/status/123456789",
      requiresConnection: true,
      socialPlatform: "twitter",
    },
    {
      id: 3,
      title: "Retweet the campaign post",
      description: "Retweet our campaign announcement to help spread the word",
      type: "twitter_retweet",
      reward: 100,
      completed: false,
      url: "https://twitter.com/BingoldOfficial/status/123456789",
      requiresConnection: true,
      socialPlatform: "twitter",
    },
    {
      id: 4,
      title: "Join our Telegram group",
      description: "Join our official Telegram group for community discussions",
      type: "telegram_join",
      reward: 100,
      completed: false,
      url: "https://t.me/bingoldofficial",
      requiresConnection: true,
      socialPlatform: "telegram",
    },
    {
      id: 5,
      title: "Invite 3 friends",
      description:
        "Invite 3 friends to join the BINGOLD platform using your referral link",
      type: "invite_friends",
      reward: 125,
      completed: false,
      requiresConnection: false,
    },
  ],
  rules: [
    "Complete all tasks to be eligible for the full reward",
    "Tasks must be completed before the campaign ends",
    "Only one entry per wallet address",
    "Fraudulent activities will result in disqualification",
    // "Rewards will be distributed within 7 days after campaign completion",
  ],
  requirements: [
    "Connect your wallet",
    "Verify your social media accounts",
    "Complete all tasks to be eligible for rewards",
  ],
};

const mockSocialConnections: SocialConnection[] = [
  { platform: "twitter", connected: false, username: "@user_bingold" },
  // { platform: "instagram", connected: false },
];

interface IndividualCampaignProps {
  campaignId?: string | null;
  onBack: () => void;
}

export default function IndividualCampaign({
  campaignId,
  onBack,
}: IndividualCampaignProps) {
  const [campaign] = useState<CampaignTest>(mockCampaign);
  const [socialConnections, setSocialConnections] = useState<
    SocialConnection[]
  >(mockSocialConnections);

  const [referralLink, setRerrakLink] = useState(
    "https://magnum.bingold.to/ref/abc123",
  );
  const [copiedReferral, setCopiedReferral] = useState(false);
  const [processingTask, setProcessingTask] = useState<number | null>(null);
  const [campaignsData, setCampaignsData] = useState<Campaign[] | null>(null);
  const [postLink, setPostLink] = useState("");

  const [isBinGold, setIsBinGold] = useState<Task | null>(null);
  const [email, setEmail] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [isOtpLoading, setIsOtpLoading] = useState(false);
  const [isVerifyLoading, setIsVerifyLoading] = useState(false);
  const [verifyingTaskId, setVerifyingTaskId] = useState<string | null>(null);
  const navigate = useNavigate();
  const completedTasks = campaign.tasks.filter((task) => task.completed);
  const totalRewardEarned = completedTasks.reduce(
    (sum, task) => sum + task.reward,
    0,
  );
  const canClaimReward = campaign.progress >= 80;

  const { data: user } = useGetProfileApiCallQuery();
  const {
    data: campaignsListTasks,
    isLoading: isCampaignsListTasksLoading,
    isError: isCampaignsListTasksError,
  } = useGetTasksApiCallQuery({
    campaignId: campaignId || "",
  });
  const { data: userProfile } = useGetProfileApiCallQuery();
  const [verifyTaks, { error }] = useVerifyTaskApiCallMutation();
  const { data: socialCredentialsTwitter, isError: isSocialCredentialsError } =
    useViewSocialsCredentialsApiCallQuery({
      platform: "Twitter",
    });

  const {
    data: socialCredentialsInstagram,
    isError: isSocialCredentialsInstagramError,
  } = useViewSocialsCredentialsApiCallQuery({
    platform: "Instagram",
  });

  const [sendOtp] = useSendOtpApiCallMutation();
  const [verifyOtp] = useVerifyOtpApiCallMutation();

  const handleVerifyTask = async (task: Task) => {
    setVerifyingTaskId(task._id); // Show loading only on this button
    try {
      if (task.platform === "BinGold") {
        setIsBinGold(task);
        return;
      }
      const response = await verifyTaks({
        taskId: task._id,
        taskUrl: postLink || undefined,
      }).unwrap();

      if (response.responseCode === 200) {
        toast.success(response.responseMessage);
      }
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      const errorMessage = errorData?.responseMessage || "Error verifying task";

      // Check if the error is about missing social account connection
      if (
        errorMessage.includes("Twitter account not attached") ||
        errorMessage.includes("Telegram account not attached") ||
        (errorMessage.toLowerCase().includes("twitter") &&
          errorMessage.toLowerCase().includes("not attached")) ||
        (errorMessage.toLowerCase().includes("telegram") &&
          errorMessage.toLowerCase().includes("not attached"))
      ) {
        toast.error("Please connect your account first", {
          description: "Go to profile settings to link your social account.",
          action: {
            label: "Go to Settings",
            onClick: () => navigate(DASHBOARD_PATH.PORFILE_SETTINGS),
          },
          duration: 8000,
        });
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setVerifyingTaskId(null);
      // Hide loading on this button
    }
  };

  const handleConnectSocial = async (
    platform: "twitter" | "telegram" | "discord" | "instagram",
  ) => {
    if (platform === "twitter") {
      const state = userProfile?.result?._id || "";
      const codeVerifier = await getCodeChallenge(state);
      const codeChallenge = codeVerifier;

      console.log(state);

      const authUrl = `https://twitter.com/i/oauth2/authorize?response_type=code&client_id=${clientId}&redirect_uri=${encodeURIComponent(
        redirectUri,
      )}&scope=tweet.read%20users.read%20like.read%20tweet.write%20follows.read%20follows.write%20offline.access&state=${state}&code_challenge=${codeChallenge}&code_challenge_method=S256`;

      window.open(authUrl, "_blank");
    } else if (platform === "instagram") {
      const state = generateRandomString(16);

      const authUrl = `https://api.instagram.com/oauth/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(
        redirectUri,
      )}&scope=${scope}&response_type=code&state=${state}`;

      window.open(authUrl, "_blank");
    }

    // Simulate connecting social media account
    // setSocialConnections((prev) =>
    //   prev.map((conn) =>
    //     conn.platform === platform
    //       ? { ...conn, connected: true, username: `@user_${platform}` }
    //       : conn
    //   )
    // );
  };

  const handleSendOtp = async () => {
    if (!email) return;
    setIsOtpLoading(true);

    // 🔹 Call your API to send OTP
    try {
      const response = await sendOtp({
        email,
      }).unwrap();

      if (response.responseCode === 200) {
        setOtpSent(true);
        toast.success(response.responseMessage);
      }
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      toast.error(errorData?.responseMessage || "Error sending OTP");
    } finally {
      setIsOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp) return;
    setIsVerifyLoading(true);

    // 🔹 Call your API to verify OTP
    try {
      const response = await verifyOtp({
        otp,
      }).unwrap();

      if (response.responseCode === 200) {
        toast.success(response.responseMessage);
        const taskResponse = await verifyTaks({
          taskId: isBinGold?._id,
          taskUrl: postLink || undefined,
          email: email,
        }).unwrap();

        if (taskResponse.responseCode === 200) {
          toast.success(taskResponse.responseMessage);
          setIsBinGold(null);
          setIsVerifyLoading(false);
        } else {
          setIsBinGold(null);
        }
      }
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      toast.error(errorData?.responseMessage || "Error verifying OTP");
      setIsVerifyLoading(false);
      setIsBinGold(null);
      setOtpSent(false);
    } finally {
      setIsVerifyLoading(false);
    }
  };

  // const handleTaskAction = async (task: Task) => {
  //   if (task.requiresConnection && task.socialPlatform) {
  //     const connection = socialConnections.find(
  //       (conn) => conn.platform === task.socialPlatform
  //     );
  //     if (!connection?.connected) {
  //       handleConnectSocial(task.socialPlatform);
  //       return;
  //     }
  //   }

  //   setProcessingTask(task.id);

  //   // Simulate task completion
  //   setTimeout(() => {
  //     // Update task completion status
  //     campaign.tasks = campaign.tasks.map((t) =>
  //       t.id === task.id ? { ...t, completed: true } : t
  //     );
  //     setProcessingTask(null);
  //   }, 2000);

  //   // Open external link if provided
  //   if (task.url) {
  //     window.open(task.url, "_blank");
  //   }
  // };

  const handleCopyReferral = () => {
    navigator.clipboard.writeText(referralLink);
    setCopiedReferral(true);
    setTimeout(() => setCopiedReferral(false), 2000);
  };

  const handleClaimReward = () => {
    if (canClaimReward) {
      // Handle reward claiming logic
      console.log("Claiming reward:", totalRewardEarned, "BINGOLD");
    }
  };

  const getTaskIcon = (type: string) => {
    console.log(type);
    switch (type) {
      case "twitter_follow":
      case "twitter_like":
      case "twitter_retweet":
      case "Twitter":
        return Twitter;
      case "telegram_join":
        return MessageCircle;
      case "discord_join":
        return UsersIcon;
      case "invite_friends":
        return UserPlus;
      default:
        return Circle;
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case "Easy":
        return "bg-green-500/20 text-green-400";
      case "Medium":
        return "bg-yellow-500/20 text-yellow-400";
      case "Hard":
        return "bg-red-500/20 text-red-400";
      default:
        return "bg-gray-500/20 text-gray-400";
    }
  };

  const isBinGoldDepositTask = (task: Task) => {
    const taskType = (task.taskType || "").toLowerCase();
    const platform = (task.platform || "").toLowerCase();
    return taskType.includes("deposit") && platform.includes("bingold");
  };

  const depositDisclaimerText =
    "Eligibility for this promotion is based on the USD equivalent value of your deposit at the time of transaction confirmation. If market volatility causes the value of your BNB or USDT to fall below the $50 threshold before the task is verified, the criteria will not be met. Participants are advised to account for market fluctuations by depositing slightly above the minimum required amount.";

  const getCampaignDescription = (description?: string) => {
    if (!description) return "";
    return description
      .replace(/Deposit Valuation Disclaimer:[\\s\\S]*/i, "")
      .trim();
  };

  useEffect(() => {
    // const ws = new WebSocket(
    //   "https://node-airdrop.bingold.to/api/v1/admin/result"
    // ); // Your backend WS URL
    // const ws = new WebSocket("ws://localhost:3000"); // Your backend WS URL

    const ws = new WebSocket("wss://node-airdrop.bingold.to"); // Your backend WS URL

    // const ws = new WebSocket("wss://node-airdrop.bingold.to"); // Your backend WS URL
    ws.onopen = () => {
      console.log("Connected to server");
      // Send the request like backend expects
      ws.send(
        JSON.stringify({
          options: "getLiveCampaigns",
          userId: user?.result?._id,
        }),
      );
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        setCampaignsData(data?.data?.docs); // store campaigns in state
      } catch (err) {
        console.error("Error parsing message:", err);
      }
    };

    ws.onerror = (err) => {
      console.error("WebSocket error:", err);
    };

    return () => {
      ws.close();
    };
  }, [user]);

  const currentCampaign = campaignsData?.filter(
    (item) => item?._id === campaignId,
  )[0];

  const { days, status } = getTimeRemaining(
    currentCampaign?.startDate || "",
    currentCampaign?.endDate || "",
  );

  useEffect(() => {
    console.log(socialCredentialsTwitter);
    if (socialCredentialsTwitter?.result) {
      setSocialConnections((prev) =>
        prev.map((conn) =>
          conn.platform === "twitter" ? { ...conn, connected: true } : conn,
        ),
      );
    } else if (socialCredentialsInstagram?.result) {
      setSocialConnections((prev) =>
        prev.map((conn) =>
          conn.platform === "instagram" ? { ...conn, connected: true } : conn,
        ),
      );
    }
  }, [socialCredentialsTwitter, socialCredentialsInstagram]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      console.log(userProfile);
      const domain = window.location.origin; // e.g. https://example.com
      setRerrakLink(`${domain}/ref/${userProfile?.result?.referralCode}`);
    }
  }, [userProfile]);

  console.log(currentCampaign);

  const { data: tokenPriceData } = useGetTokenPriceConversionApiCallQuery();

  const getUSDTBalance = (walletBalance: number) => {
    const usdtPrice =
      tokenPriceData?.result?.data?.find((item: any) => item.coin === "usdt")
        ?.tokenPrice || 0;
    const balance = walletBalance * usdtPrice || 0;
    return balance;
  };

  const usdtReward = getUSDTBalance(
    Number(currentCampaign?.totalRewardOfTasks),
  );
  if (isCampaignsListTasksLoading) {
    return (
      <div className="fixed inset-0 bg-[#050505] flex items-center justify-center z-50">
        <Loader size="lg" text="Loading campaign details..." />
      </div>
    );
  }
  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="text-white hover:bg-secondary"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-white">
              {currentCampaign?.name}
            </h1>
            <p className="text-muted-foreground">Campaign Details & Tasks</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Campaign Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Campaign Overview */}
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <Badge className="bg-yellow-500/20 text-yellow-400 border-0">
                        {currentCampaign?.status}
                      </Badge>
                      {/* <Badge
                        className={`${getDifficultyColor(
                          campaign.difficulty
                        )} border-0`}
                      >
                        {campaign.difficulty}
                      </Badge> */}
                    </div>
                    <p className="text-muted-foreground leading-relaxed">
                      {getCampaignDescription(currentCampaign?.description)}
                    </p>
                  </div>
                </div>

                {/* Campaign Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-secondary/50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-primary mb-1 break-words">
                      {currentCampaign?.totalRewardOfTasks || 0}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Your Reward
                    </div>
                    <div className="flex items-center justify-center gap-1 text-xs">
                      <span className="font-semibold text-green-400">
                        ${usdtReward ? usdtReward.toFixed(2) : 0}
                      </span>
                    </div>
                  </div>
                  <div className="bg-secondary/50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-white mb-1">
                      {currentCampaign?.airdropAmount || 0}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Total Pool
                    </div>
                  </div>
                  <div className="bg-secondary/50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-white mb-1">
                      {currentCampaign?.totalJoined || 0}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Participants
                    </div>
                  </div>
                  <div className="bg-secondary/50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-white mb-1">
                      {status === "ended"
                        ? "Ended"
                        : `${!isNaN(days) ? `${days} days left` : ""} `}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Time Left
                    </div>
                  </div>
                </div>

                {/* Progress */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-white">
                      Overall Progress
                    </span>
                    <span className="font-semibold text-white">
                      {Math.floor(
                        (currentCampaign?.allVerifiedTasks || 0) /
                          (currentCampaign?.allTasks || 0.5),
                      ) * 100}
                      %
                    </span>
                  </div>
                  <Progress
                    value={
                      Math.floor(
                        (currentCampaign?.allVerifiedTasks || 0) /
                          (currentCampaign?.allTasks || 0),
                      ) * 100
                    }
                    className="h-3 bg-secondary"
                  />
                  <div className="flex justify-between mt-2 text-sm text-muted-foreground">
                    <span>
                      {currentCampaign?.allVerifiedTasks || 0}/
                      {currentCampaign?.allTasks || 0} tasks completed
                    </span>
                    <span>
                      {currentCampaign?.totalEarned || 0}/
                      {currentCampaign?.totalRewardOfTasks || 0} BINGOLD earned
                    </span>
                  </div>
                </div>

                {/* Claim Reward Button */}
                {canClaimReward && (
                  <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-white mb-1">
                          Ready to Claim!
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          You've completed enough tasks to claim your rewards
                        </p>
                      </div>
                      <Button
                        onClick={handleClaimReward}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                      >
                        <Coins className="h-4 w-4 mr-2" />
                        Claim {totalRewardEarned} BINGOLD
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tasks List */}
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold text-white mb-6">
                  Campaign Tasks
                </h3>
                {/* <div className="space-y-4">
                  {campaign.tasks.map((task) => {
                    const TaskIcon = getTaskIcon(task.type);
                    const isProcessing = processingTask === task.id;
                    const needsConnection =
                      task.requiresConnection &&
                      !socialConnections.find(
                        (conn) => conn.platform === task.socialPlatform
                      )?.connected;

                    return (
                      <div
                        key={task.id}
                        className={`border rounded-lg p-4 transition-all ${
                          task.completed
                            ? "border-green-500/30 bg-green-500/5"
                            : "border-border hover:border-primary/30"
                        }`}
                      >
                        <div className="flex items-start space-x-4">
                          <div
                            className={`flex items-center justify-center h-10 w-10 rounded-full ${
                              task.completed
                                ? "bg-green-500/20 text-green-400"
                                : "bg-secondary text-muted-foreground"
                            }`}
                          >
                            {task.completed ? (
                              <CheckCircle className="h-5 w-5" />
                            ) : (
                              <TaskIcon className="h-5 w-5" />
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between">
                              <div>
                                <h4 className="font-medium text-white mb-1">
                                  {task.title}
                                </h4>
                                <p className="text-sm text-muted-foreground mb-2">
                                  {task.description}
                                </p>
                                <div className="flex items-center space-x-4">
                                  <span className="text-sm font-medium text-primary">
                                    +{task.reward} BINGOLD
                                  </span>
                                  {task.completed && (
                                    <span className="text-sm text-green-400 flex items-center">
                                      <CheckCheck className="h-4 w-4 mr-1" />
                                      Completed
                                    </span>
                                  )}
                                </div>
                              </div>

                              <div className="flex-shrink-0 ml-4">
                                {task.completed ? (
                                  <Badge className="bg-green-500/20 text-green-400 border-0">
                                    Completed
                                  </Badge>
                                ) : needsConnection ? (
                                  <Button
                                    size="sm"
                                    onClick={() =>
                                      handleConnectSocial(task.socialPlatform!)
                                    }
                                    className="bg-primary hover:bg-primary/90 text-primary-foreground"
                                  >
                                    Connect {task.socialPlatform}
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    onClick={() => handleTaskAction(task)}
                                    disabled={isProcessing}
                                    className="bg-primary hover:bg-primary/90 text-primary-foreground"
                                  >
                                    {isProcessing
                                      ? "Processing..."
                                      : "Complete Task"}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div> */}

                <div className="space-y-4">
                  {isCampaignsListTasksLoading ? (
                    <p>Loading...</p>
                  ) : (
                    campaignsListTasks?.result &&
                    campaignsListTasks?.result?.docs?.length > 0 &&
                    campaignsListTasks?.result?.docs?.map((task) => {
                      const TaskIcon = getTaskIcon(task.platform);
                      const isProcessing = processingTask === (task._id as any);
                      const isPost = task.taskType === "Post";
                      const isVerifying = verifyingTaskId === task._id;
                      // const needsConnection =
                      //   (task.requiresConnection || ) &&
                      //   !socialConnections.find(
                      //     (conn) => conn.platform === task.socialPlatform
                      //   )?.connected;

                      return (
                        <div
                          key={task._id}
                          className={`border rounded-lg p-4 transition-all ${
                            task.isVerified
                              ? "border-green-500/30 bg-green-500/5"
                              : "border-border hover:border-primary/30"
                          }`}
                        >
                          <div className="flex flex-col sm:flex-row items-start sm:space-x-4 space-y-4 sm:space-y-0">
                            <div
                              className={`flex items-center justify-center h-10 w-10 rounded-full ${
                                task.isVerified
                                  ? "bg-green-500/20 text-green-400"
                                  : "bg-secondary text-muted-foreground"
                              }`}
                            >
                              {task.isVerified ? (
                                <CheckCircle className="h-5 w-5" />
                              ) : (
                                <TaskIcon className="h-5 w-5" />
                              )}
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                                <div>
                                  <h4 className="font-medium text-white mb-1">
                                    {`${task.taskType} on ${task.platform}`}
                                  </h4>
                                  {!isBinGoldDepositTask(task) && (
                                    <p className="text-sm text-muted-foreground mb-2">
                                      {task.instructions}
                                    </p>
                                  )}

                                  {isPost && (
                                    <div className="flex items-center gap-2">
                                      <p>{task.text}</p>
                                      <span
                                        className="cursor-pointer"
                                        onClick={() =>
                                          navigator.clipboard.writeText(
                                            task.text,
                                          )
                                        }
                                      >
                                        <CopyIcon size={16} />
                                      </span>
                                    </div>
                                  )}

                                  <div className="flex items-center space-x-4">
                                    {/* <span className="text-sm font-medium text-primary">
                                      +{task.reward} BINGOLD
                                    </span> */}
                                    {task.isVerified && (
                                      <span className="text-sm text-green-400 flex items-center">
                                        <CheckCheck className="h-4 w-4 mr-1" />
                                        Completed
                                      </span>
                                    )}
                                  </div>

                                  {isPost && (
                                    <div className="mt-4">
                                      <Input
                                        placeholder="Enter your post link"
                                        value={postLink}
                                        onChange={(e) =>
                                          setPostLink(e.target.value)
                                        }
                                      />
                                    </div>
                                  )}
                                </div>

                                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:ml-4 w-full sm:w-auto">
                                  {task.isVerified ? (
                                    <Badge className="bg-green-500/20 text-green-400 border-0">
                                      Completed
                                    </Badge>
                                  ) : (
                                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4  w-full sm:w-auto">
                                      {!isPost && (
                                        <Link
                                          to={task?.url}
                                          target="_blank"
                                          className="w-full sm:w-auto"
                                        >
                                          <Button
                                            size="sm"
                                            // onClick={() =>
                                            //   handleConnectSocial(
                                            //     task.socialPlatform!
                                            //   )
                                            // }

                                            className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground cursor-pointer"
                                          >
                                            Go to task
                                          </Button>
                                        </Link>
                                      )}

                                      {isPost ? (
                                        <Button
                                          size="sm"
                                          onClick={() => handleVerifyTask(task)}
                                          disabled={isProcessing || !postLink}
                                          className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground cursor-pointer"
                                        >
                                          {isVerifying
                                            ? "Verifying..."
                                            : "Verify"}
                                        </Button>
                                      ) : (
                                        <Button
                                          size="sm"
                                          onClick={() => handleVerifyTask(task)}
                                          disabled={isProcessing}
                                          className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground cursor-pointer"
                                        >
                                          {isVerifying
                                            ? "Verifying..."
                                            : "Verify"}
                                        </Button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {isBinGoldDepositTask(task) && (
                                <div className="mt-3 w-full rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 sm:p-4 overflow-hidden">
                                  <div className="mb-2 flex items-start gap-2">
                                    <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-300" />
                                    <p className="text-xs sm:text-sm font-semibold text-amber-200">
                                      Deposit Valuation Disclaimer
                                    </p>
                                  </div>
                                  <p className="text-xs sm:text-sm leading-relaxed text-amber-100/90 break-words whitespace-normal">
                                    {depositDisclaimerText}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {!isCampaignsListTasksLoading &&
                  campaignsListTasks?.result?.docs?.length === 0 && (
                    <p className="text-white">No tasks found</p>
                  )}
              </CardContent>
            </Card>

            <Dialog open={!!isBinGold} onOpenChange={() => setIsBinGold(null)}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Verify Email</DialogTitle>
                  <DialogDescription>
                    Please enter your email to receive an OTP for verification.
                  </DialogDescription>
                </DialogHeader>

                {!otpSent ? (
                  <div className="space-y-4">
                    <Input
                      type="email"
                      placeholder="Enter email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <Button
                      className="w-full"
                      onClick={handleSendOtp}
                      disabled={!email || isOtpLoading}
                    >
                      {isOtpLoading ? "Sending..." : "Send OTP"}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Input
                      type="text"
                      placeholder="Enter OTP"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                    />
                    <Button
                      className="w-full"
                      onClick={handleVerifyOtp}
                      disabled={!otp || isVerifyLoading}
                    >
                      {isVerifyLoading ? "Verifying..." : "Verify OTP"}
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Social Connections */}
            {/* <Card className="bg-card border-border">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4">
                  Social Connections
                </h3>
                <div className="space-y-3">
                  {socialConnections.map((connection) => (
                    <div
                      key={connection.platform}
                      className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="text-primary">
                          {connection.platform === "twitter" && (
                            <Twitter className="h-5 w-5" />
                          )}
                          {connection.platform === "telegram" && (
                            <MessageCircle className="h-5 w-5" />
                          )}
                          {connection.platform === "instagram" && (
                            <Instagram className="h-5 w-5" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-white capitalize">
                            {connection.platform}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {connection.connected
                              ? connection.username
                              : "Not connected"}
                          </p>
                        </div>
                      </div>
                      {connection.connected ? (
                        <Badge className="bg-green-500/20 text-green-400 border-0">
                          Connected
                        </Badge>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() =>
                            handleConnectSocial(connection.platform)
                          }
                          className="bg-primary hover:bg-primary/90 text-primary-foreground cursor-pointer"
                        >
                          Connect
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card> */}

            {/* Referral Link */}
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4">
                  Referral Link
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Invite friends and earn bonus rewards
                </p>
                <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
                  {" "}
                  <div className="flex-1 bg-secondary/50 rounded-lg p-3">
                    <p className="text-sm text-white truncate">
                      {referralLink}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={handleCopyReferral}
                    className={`cursor-pointer ${
                      copiedReferral
                        ? "bg-green-500/20 text-green-400 border border-green-500/30"
                        : "bg-primary hover:bg-primary/90 text-primary-foreground"
                    }`}
                  >
                    {copiedReferral ? (
                      <CheckCheck className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Campaign Rules */}
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4">
                  Campaign Rules
                </h3>
                <ul className="space-y-2">
                  {campaign?.rules.map((rule, index) => (
                    <li
                      key={index}
                      className="text-sm text-muted-foreground flex items-start"
                    >
                      <span className="text-primary mr-2">•</span>
                      {rule}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Requirements */}
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4">
                  Requirements
                </h3>
                <ul className="space-y-2">
                  {campaign.requirements.map((requirement, index: number) => (
                    <li
                      key={index}
                      className="text-sm text-muted-foreground flex items-start"
                    >
                      <AlertCircle className="h-4 w-4 text-yellow-400 mr-2 mt-0.5 flex-shrink-0" />
                      {requirement}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
